import React from 'react';
import { Helmet } from 'react-helmet-async';

const EnhancedSEOHead = ({
  title,
  description,
  keywords = '',
  ogImage = '/assets/images/og-default.jpg',
  canonicalUrl,
  schemaData,
  breadcrumbs,
  meta,
  pageType = 'website',
  publishDate,
  modifiedDate,
  author = 'Bootminds'
}) => {
  const siteUrl = import.meta.env.VITE_APP_SITE_URL || 'https://bootminds.com';
  const fullCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : `${siteUrl}${window.location.pathname}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  // Enhanced Organization Schema with comprehensive details
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Bootminds",
    "description": "Bootminds helps global enterprises launch and expand India GCCs with transparent pricing, zero-risk engagement, and SkillStack™ staffing.",
    "url": siteUrl,
    "logo": `${siteUrl}/assets/images/White_logo_Bootminds.png`,
    "image": `${siteUrl}/assets/images/og-default.jpg`,
    "foundingDate": "2017",
    "legalName": "BootMinds Systems Pvt Ltd.",
    "numberOfEmployees": "50-100",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN",
      "addressRegion": "Karnataka",
      "addressLocality": "Bangalore"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "Executive Contact",
        "email": "executive@bootminds.com",
        "url": `${siteUrl}/contact`
      },
      {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "url": `${siteUrl}/contact`
      }
    ],
    "sameAs": [
      "https://www.linkedin.com/company/bootminds-elite"
    ],
    "knowsAbout": [
      "Global Capability Center Setup",
      "GCC Operations",
      "India Business Setup",
      "Offshore Development",
      "Digital Transformation",
      "Talent Acquisition",
      "Strategic Consulting"
    ],
    "serviceArea": {
      "@type": "Place",
      "name": "India"
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "United States"
      },
      {
        "@type": "Country", 
        "name": "United Kingdom"
      },
      {
        "@type": "Country",
        "name": "Germany"
      },
      {
        "@type": "Country",
        "name": "Australia"
      }
    ]
  };

  // Enhanced Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": {
        "@type": "WebPage",
        "@id": `${siteUrl}${crumb.url}`,
        "url": `${siteUrl}${crumb.url}`,
        "name": crumb.name
      }
    }))
  } : null;

  // Website Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Bootminds",
    "url": siteUrl,
    "description": "Bootminds helps global enterprises launch and expand India GCCs with transparent pricing, zero-risk engagement, and SkillStack™ staffing.",
    "publisher": {
      "@type": "Organization",
      "name": "Bootminds",
      "logo": `${siteUrl}/assets/images/White_logo_Bootminds.png`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // FAQ Schema for common pages
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a Global Capability Center (GCC)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A Global Capability Center (GCC) is a strategic offshore operation that provides specialized services, innovation capabilities, and operational excellence to multinational corporations, typically established in cost-effective locations like India."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to set up a GCC in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "With Bootminds's proven methodology, GCC setup typically takes 90-120 days from initial planning to operational launch, including legal setup, talent acquisition, infrastructure, and compliance requirements."
        }
      },
      {
        "@type": "Question",
        "name": "What are the key benefits of establishing a GCC in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key benefits include 50-60% cost savings vs US operations (Zinnov GCC Cost Index 2024), access to world-class talent pool, 24/7 operational capabilities, favorable government policies, robust IT infrastructure, and English-speaking workforce with strong technical skills."
        }
      }
    ]
  };

  // Service Schema for service pages
  const serviceSchema = pageType === 'service' ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": title,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": "Bootminds",
      "url": siteUrl
    },
    "serviceType": "Global Capability Center Consulting",
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "GCC Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "GCC Setup Services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Market Intelligence"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "ROI Calculator Tools"
          }
        }
      ]
    }
  } : null;

  return (
    <Helmet>
      {/* Enhanced Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Enhanced Meta Tags */}
      <meta name="author" content={author} />
      <meta name="language" content="en" />
      <meta name="revisit-after" content="7 days" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Article specific meta tags */}
      {publishDate && <meta name="article:published_time" content={publishDate} />}
      {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
      <meta name="article:author" content={author} />
      
      {/* Enhanced Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - Bootminds`} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:type" content={pageType} />
      <meta property="og:site_name" content="Bootminds" />
      <meta property="og:locale" content="en_US" />
      
      {/* Enhanced Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={`${title} - Bootminds`} />
      <meta name="twitter:site" content="@BootmindsElite" />
      <meta name="twitter:creator" content="@BootmindsElite" />

      {/* LinkedIn Meta Tags */}
      <meta property="linkedin:owner" content="bootminds-elite" />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#1e293b" />
      <meta name="msapplication-TileColor" content="#1e293b" />
      <meta name="application-name" content="Bootminds" />
      
      {/* Geo Meta Tags */}
      <meta name="geo.region" content="IN-KA" />
      <meta name="geo.placename" content="Bangalore" />
      <meta name="geo.position" content="12.9716;77.5946" />
      <meta name="ICBM" content="12.9716, 77.5946" />

      {/* Enhanced JSON-LD Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}

      {(window.location.pathname === '/' || 
        window.location.pathname.includes('about') || 
        window.location.pathname.includes('contact')) && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}

      {/* Custom meta tags */}
      {meta && meta.map((item, index) => (
        <meta key={index} {...item} />
      ))}
    </Helmet>
  );
};

export default EnhancedSEOHead;